import { useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

export default function Blog() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const { data: posts = [] } = useQuery({ queryKey: ["blog"], queryFn: api.getBlog });
  const [cat, setCat] = useState("all");

  const cats = ["all", ...Array.from(new Set(posts.map((p) => p.category)))];
  const filtered = cat === "all" ? posts : posts.filter((p) => p.category === cat);

  const meta = {
    da: { title: "Viden om SEO | Artikler | Magnus Løv Schmidt", h: ["Viden direkte", "til dig"], d: "Artikler om SEO, teknik, strategi og alt det, der giver mere omsætning gennem organisk vækst." },
    en: { title: "SEO knowledge | Articles | Magnus Løv Schmidt", h: ["Knowledge", "straight to you"], d: "Articles on SEO, technique, strategy and everything that drives more revenue through organic growth." },
  }[lang];

  return (
    <>
      <Seo routeKey="blog" lang={lang} title={meta.title} description={meta.d}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_blog"), path: buildPath("blog", lang) }])]} />
      <PageHero title={meta.h} description={meta.d}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_blog") }]} />

      <section className="px-6 md:px-12 lg:px-20 py-12">
        <div className="mb-10 flex flex-wrap gap-2">
          {cats.map((cc) => (
            <button key={cc} onClick={() => setCat(cc)} data-testid={`blog-filter-${cc}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${cat === cc ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {cc === "all" ? (lang === "da" ? "Alle" : "All") : cc}
            </button>
          ))}
        </div>

        <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            const c = p[lang] || p.da;
            return (
              <StaggerItem key={p.slug}>
                <Link to={buildPath("post", lang, { slug: p.slug })} data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card card-hover">
                  <div className="grayscale-hover aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="rounded-full bg-secondary px-2.5 py-1 text-primary">{p.category}</span>
                      <span className="font-mono">{p.read_time} {t("min_read")}</span>
                    </div>
                    <h2 className="mt-4 font-heading text-xl font-bold tracking-tight leading-snug">{c.title}</h2>
                    <p className="mt-2 flex-1 text-sm text-muted-foreground font-light line-clamp-3">{c.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">{t("read_article")} <ArrowUpRight size={15} weight="bold" /></span>
                  </div>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
